import React, { useState } from 'react';
import Dropdown from '../../../components/Dropdown/Dropdown';
import styles from './AppointmentControls.module.css';

// AB#1901 — Per Faris arch-brief (docs/features/AB1894/arch-brief.md §3.1 W-1),
// the BE serializes AppointmentStatus and PaymentStatus as STRING names via
// the global JsonStringEnumConverter. This control was previously emitting
// the legacy integer ordinals (PaymentStatus.Unpaid = 2) which the downstream
// filter in Appointments.tsx then compared against the wire string ("Unpaid"),
// causing every filter to silently drop all rows. The fix: emit the BE wire
// string so downstream string-compares match. Mirrors the convention already
// in place in `Owner/Appointments/AppointmentControls.tsx`.

const paymentStatusOptions: ReadonlyArray<{ label: string; value: string | null }> = [
    { label: 'All', value: null },
    { label: 'Paid', value: 'Paid' },
    { label: 'Unpaid', value: 'Unpaid' },
    { label: 'Upcoming', value: 'Upcoming' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Late', value: 'Late' }
];

const appointmentStatusOptions: ReadonlyArray<{ label: string; value: string | null }> = [
    { label: 'All', value: null },
    { label: 'Pending', value: 'Pending' },
    { label: 'Confirmed', value: 'Approved' },     // FE "Confirmed" = BE "Approved"
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' }
];

interface AppointmentControlsProps {
    onPaymentStatusChange?: (status: string | null) => void;
    onStatusChange?: (status: string | null) => void;
}

const AppointmentControls: React.FC<AppointmentControlsProps> = ({
    onPaymentStatusChange,
    onStatusChange
}) => {
    const [payment, setPayment] = useState('All');
    const [status, setStatus] = useState('All');

    const handlePaymentChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            setPayment(value);
            if (onPaymentStatusChange) {
                const selectedStatus = paymentStatusOptions.find(s => s.label === value);
                onPaymentStatusChange(selectedStatus?.value ?? null);
            }
        } else if (Array.isArray(value) && value.length > 0) {
            setPayment(value[0]);
        }
    };

    const handleStatusChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            setStatus(value);
            if (onStatusChange) {
                const selectedStatus = appointmentStatusOptions.find(s => s.label === value);
                onStatusChange(selectedStatus?.value ?? null);
            }
        } else if (Array.isArray(value) && value.length > 0) {
            setStatus(value[0]);
        }
    };

    return (
        <div className={styles.controls}>
            <div className={styles.controllerContainer}>
                Status
                <Dropdown
                    options={appointmentStatusOptions.map(s => s.label)}
                    value={status}
                    onChange={handleStatusChange}
                />
            </div>
            <div className={styles.controllerContainer}>
                Payment
                <Dropdown
                    options={paymentStatusOptions.map(s => s.label)}
                    value={payment}
                    onChange={handlePaymentChange}
                />
            </div>
        </div>
    );
};

export default AppointmentControls;
